/** Date display (docs/14: dates in IBM Plex Mono). Inputs are ISO-8601 strings in UTC (docs/08). */
export type DateInput = string | Date | null | undefined;

const toDate = (v: DateInput): Date | null => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** `14 Oct 2026` */
export function formatDate(v: DateInput, options: { locale?: string; timeZone?: string } = {}): string {
  const d = toDate(v);
  if (!d) return '—';
  return new Intl.DateTimeFormat(options.locale ?? 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
  }).format(d);
}

/** `14 Oct 2026, 09:00` in the given zone (event times use the event's IANA zone). */
export function formatDateTime(v: DateInput, options: { locale?: string; timeZone?: string } = {}): string {
  const d = toDate(v);
  if (!d) return '—';
  return new Intl.DateTimeFormat(options.locale ?? 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(options.timeZone ? { timeZone: options.timeZone, timeZoneName: 'short' } : {}),
  }).format(d);
}

/** `3 d ago`, `in 2 h`, `now`. */
export function formatRelative(v: DateInput, now: Date = new Date(), locale = 'en'): string {
  const d = toDate(v);
  if (!d) return '—';
  const diff = (d.getTime() - now.getTime()) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' });
  if (abs < 45) return rtf.format(0, 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 30 * 86_400) return rtf.format(Math.round(diff / 86_400), 'day');
  return formatDate(d);
}
